// src/pages/Consultation/SessionDetail/Tabs/CartTab.jsx
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FiSearch,
  FiPlus,
  FiShoppingCart,
  FiEye,
  FiTrash2,
} from "react-icons/fi";
import {
  addItemToCartThunk,
  removeItemFromCartThunk,
  updateItemQuantityThunk,
  showCartToCustomerThunk,
  calculatePricingThunk,
} from "../../../../store/thunks/consultationThunks";
import { getAllProductsThunk } from "../../../../store/thunks/adminProductThunks";
import Button from "../../../../components/common/Button";
import toast from "react-hot-toast";

const CartTab = ({ session }) => {
  const dispatch = useDispatch();

  const { sessionCart, loadingCart, sessionCapabilities } = useSelector(
    (state) => state.consultation
  );
  const { products, loading: loadingProducts } = useSelector(
    (state) => state.adminProduct
  );
  const { user } = useSelector((state) => state.auth);

  const [productSearch, setProductSearch] = useState("");
  const [searchFilters, setSearchFilters] = useState({
    type: "",
    inStock: "true",
  });

  // Search products
  const handleSearchProducts = () => {
    dispatch(
      getAllProductsThunk({
        search: productSearch,
        ...searchFilters,
        branchCode: session.branchCode,
        limit: 12,
      })
    );
  };

  console.log("sessionCapabilities", sessionCapabilities);
  console.log("sessionCart", sessionCart);
  console.log("session", session);

  // Add to cart
  const handleAddToCart = async (product) => {
    if (!sessionCapabilities.canModifyCart) {
      toast.error("Cannot modify cart in current session state");
      return;
    }

    try {
      await dispatch(
        addItemToCartThunk({
          sessionId: session._id,
          itemData: {
            productId: product._id,
            quantity: 1,
            adminCode: user?.adminCode || user?._id,
            adminName: user?.name,
          },
        })
      ).unwrap();

      toast.success("Item added to cart");
    } catch (error) {
      toast.error(error || "Failed to add item");
    }
  };

  // Remove from cart
  const handleRemoveItem = async (productId) => {
    try {
      await dispatch(
        removeItemFromCartThunk({
          sessionId: session._id,
          productId,
          adminCode: user?.adminCode || user?._id,
          adminName: user?.name,
        })
      ).unwrap();

      toast.success("Item removed from cart");
    } catch (error) {
      toast.error(error || "Failed to remove item");
    }
  };

  // Update quantity
  const handleUpdateQuantity = async (productId, newQuantity) => {
    if (newQuantity < 1) return;

    try {
      await dispatch(
        updateItemQuantityThunk({
          sessionId: session._id,
          productId,
          quantity: newQuantity,
          adminCode: user?.adminCode || user?._id,
          adminName: user?.name,
        })
      ).unwrap();
    } catch (error) {
      toast.error(error || "Failed to update quantity");
    }
  };

  // Show cart to customer
  const handleShowCart = async () => {
    try {
      await dispatch(
        showCartToCustomerThunk({
          sessionId: session._id,
          adminCode: user?.adminCode || user?._id,
          adminName: user?.name,
        })
      ).unwrap();

      toast.success("Cart shown to customer");
    } catch (error) {
      toast.error(error || "Failed to show cart");
    }
  };

  // Calculate pricing
  const handleCalculatePricing = async () => {
    try {
      await dispatch(calculatePricingThunk(session._id)).unwrap();
      toast.success("Pricing calculated");
    } catch (error) {
      toast.error(error || "Failed to calculate pricing");
    }
  };

  const cartItems = sessionCart?.items || [];
  const cartSummary = sessionCart?.summary || {};
  const totalItems = cartSummary.totalItems || 0;
  const netPayable = cartSummary.netPayable || 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Product Search - Left Side */}
      <div className="lg:col-span-2 space-y-4">
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-medium text-gray-900 mb-4">Product Search</h3>

          {/* Search Bar */}
          <div className="flex gap-2 mb-4">
            <div className="flex-1 relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearchProducts()}
                placeholder="Search products..."
                className="w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <Button onClick={handleSearchProducts} disabled={loadingProducts}>
              Search
            </Button>
          </div>

          {/* Quick Filters */}
          <div className="flex gap-2 flex-wrap">
            <select
              value={searchFilters.type}
              onChange={(e) =>
                setSearchFilters((prev) => ({ ...prev, type: e.target.value }))
              }
              className="px-3 py-1 text-sm border rounded-lg"
            >
              <option value="">All Types</option>
              <option value="GOLD">Gold</option>
              <option value="SILVER">Silver</option>
              <option value="PLATINUM">Platinum</option>
              <option value="DIAMOND">Diamond</option>
            </select>

            <select
              value={searchFilters.inStock}
              onChange={(e) =>
                setSearchFilters((prev) => ({
                  ...prev,
                  inStock: e.target.value,
                }))
              }
              className="px-3 py-1 text-sm border rounded-lg"
            >
              <option value="">All Stock</option>
              <option value="true">In Stock Only</option>
              <option value="false">Out of Stock</option>
            </select>
          </div>
        </div>

        {/* Products Grid */}
        {/* Products Grid */}
        {loadingProducts ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg">
            <p className="text-gray-500">No products found. Try searching...</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {products.map((product) => {
              // Get branch-specific pricing
              const branchPricing =
                product.pricing?.branches?.[session.branchCode];
              const branchStock = product.stock?.locationStock?.find(
                (loc) => loc.branchCode === session.branchCode
              );
              const availableQty = branchStock?.available || 0;
              const isAvailable = availableQty > 0;
              const isLowStock = availableQty > 0 && availableQty <= 5;

              return (
                <div
                  key={product._id}
                  className={`bg-white rounded-lg shadow hover:shadow-md transition-shadow p-3 ${
                    !isAvailable ? "opacity-60" : ""
                  }`}
                >
                  {/* Product Image */}
                  <div className="aspect-square bg-gray-100 rounded-lg mb-2 overflow-hidden relative">
                    {product.media?.[0] ? (
                      <img
                        src={product.media[0].url}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FiShoppingCart className="text-3xl text-gray-400" />
                      </div>
                    )}

                    {/* Stock Badge */}
                    {!isAvailable && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <span className="bg-red-500 text-white text-xs px-2 py-1 rounded">
                          Out of Stock
                        </span>
                      </div>
                    )}
                    {isLowStock && (
                      <div className="absolute top-2 right-2">
                        <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded">
                          Low Stock
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="space-y-1">
                    <h4 className="font-medium text-sm line-clamp-1">
                      {product.name}
                    </h4>
                    <p className="text-xs text-gray-500">{product.productId}</p>

                    {/* Metal Details */}
                    <div className="flex items-center justify-between text-xs text-gray-600">
                      <span>{product.grossWeight}g</span>
                      <span>
                        {product.metalDetails.purity}{" "}
                        {product.metalDetails.color}
                      </span>
                    </div>

                    {/* Stock Info */}
                    <div className="flex items-center justify-between text-xs">
                      <span
                        className={
                          isLowStock ? "text-orange-600" : "text-gray-600"
                        }
                      >
                        Stock: {availableQty}
                      </span>
                      {branchStock?.branchCode && (
                        <span className="text-gray-500">
                          {branchStock.branchCode}
                        </span>
                      )}
                    </div>

                    {/* Pricing */}
                    {branchPricing ? (
                      <div className="pt-2 border-t">
                        <div className="text-xs text-gray-500 mb-1">
                          Metal: ₹
                          {branchPricing.breakdown.metalValue.toLocaleString(
                            "en-IN"
                          )}
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-600">
                            +Making/GST
                          </span>
                          <span className="text-sm font-bold text-primary">
                            ₹
                            {branchPricing.breakdown.finalAmount.toLocaleString(
                              "en-IN"
                            )}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="pt-2 text-xs text-red-500">
                        Price not available at this branch
                      </div>
                    )}
                  </div>

                  {/* Add Button */}
                  <Button
                    size="sm"
                    onClick={() => handleAddToCart(product)}
                    disabled={
                      !sessionCapabilities.canModifyCart ||
                      !isAvailable ||
                      !branchPricing
                    }
                    className="w-full mt-3"
                  >
                    <FiPlus className="mr-1" />
                    {!isAvailable ? "Out of Stock" : "Add to Cart"}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Cart Summary - Right Side (Sticky) */}
      <div className="lg:col-span-1">
        <div className="bg-white rounded-lg shadow p-4 sticky top-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-gray-900">Cart Summary</h3>
            <span className="px-2 py-1 bg-primary/10 text-primary text-sm rounded-full">
              {totalItems} items
            </span>
          </div>

          {/* Cart Items */}
          {cartItems.length === 0 ? (
            <div className="text-center py-8">
              <FiShoppingCart className="mx-auto text-4xl text-gray-300 mb-2" />
              <p className="text-sm text-gray-500">Cart is empty</p>
            </div>
          ) : (
            <div className="space-y-3 mb-4 max-h-96 overflow-y-auto">
              {cartItems.map((item) => (
                <div
                  key={item._id}
                  className="p-3 border border-gray-200 rounded-lg"
                >
                  {/* Product Image */}
                  {item.product.images?.[0] && (
                    <div className="w-full h-20 bg-gray-100 rounded mb-2 overflow-hidden">
                      <img
                        src={item.product.images[0].url}
                        alt={item.product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <h4 className="text-sm font-medium line-clamp-1">
                        {item.product.name}
                      </h4>
                      <p className="text-xs text-gray-500">
                        {item.product.productId}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        {item.product.grossWeight}g •{" "}
                        {item.product.metalDetails.purity}{" "}
                        {item.product.metalDetails.color}
                      </p>
                    </div>
                    <button
                      onClick={() => handleRemoveItem(item.product._id)}
                      className="text-red-500 hover:text-red-700 p-1"
                      disabled={!sessionCapabilities.canModifyCart}
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </div>

                  {/* Pricing Breakdown */}
                  <div className="bg-gray-50 rounded p-2 mb-2 text-xs space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Metal Value:</span>
                      <span>
                        ₹
                        {item.pricing.breakdown.metalValue.toLocaleString(
                          "en-IN"
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Making:</span>
                      <span>
                        ₹
                        {item.pricing.breakdown.makingCharges.toLocaleString(
                          "en-IN"
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Wastage:</span>
                      <span>
                        ₹
                        {item.pricing.breakdown.wastageCharges.toLocaleString(
                          "en-IN"
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        GST ({item.pricing.breakdown.gst.rate}%):
                      </span>
                      <span>
                        ₹
                        {item.pricing.breakdown.gst.amount.toLocaleString(
                          "en-IN"
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() =>
                          handleUpdateQuantity(
                            item.product._id,
                            item.quantity - 1
                          )
                        }
                        disabled={
                          !sessionCapabilities.canModifyCart ||
                          item.quantity <= 1
                        }
                        className="w-6 h-6 flex items-center justify-center border rounded text-sm disabled:opacity-50"
                      >
                        -
                      </button>
                      <span className="text-sm w-8 text-center">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() =>
                          handleUpdateQuantity(
                            item.product._id,
                            item.quantity + 1
                          )
                        }
                        disabled={!sessionCapabilities.canModifyCart}
                        className="w-6 h-6 flex items-center justify-center border rounded text-sm disabled:opacity-50"
                      >
                        +
                      </button>
                    </div>

                    <div className="text-right">
                      <div className="text-xs text-gray-500">
                        ₹{item.pricing.unitPrice.toLocaleString("en-IN")} each
                      </div>
                      <span className="text-sm font-medium text-primary">
                        ₹{item.pricing.totalPrice.toLocaleString("en-IN")}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Detailed Cart Summary */}
          {cartItems.length > 0 && (
            <div className="border-t pt-3 mb-4 space-y-2 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Metal Value:</span>
                <span>
                  ₹{cartSummary.totalMetalValue?.toLocaleString("en-IN")}
                </span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Making Charges:</span>
                <span>
                  ₹{cartSummary.totalMakingCharges?.toLocaleString("en-IN")}
                </span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Wastage:</span>
                <span>
                  ₹{cartSummary.totalWastageCharges?.toLocaleString("en-IN")}
                </span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Subtotal:</span>
                <span>₹{cartSummary.subtotal?.toLocaleString("en-IN")}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>GST:</span>
                <span>₹{cartSummary.totalGST?.toLocaleString("en-IN")}</span>
              </div>

              {cartSummary.totalDiscount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Discount:</span>
                  <span>
                    -₹{cartSummary.totalDiscount?.toLocaleString("en-IN")}
                  </span>
                </div>
              )}

              <div className="border-t pt-2 flex justify-between items-center font-bold">
                <span className="text-gray-900">Net Payable:</span>
                <span className="text-xl text-primary">
                  ₹{netPayable.toLocaleString("en-IN")}
                </span>
              </div>

              <div className="flex justify-between text-xs text-gray-500 pt-1">
                <span>Total Weight:</span>
                <span>
                  {cartSummary.totalGrossWeight}g gross /{" "}
                  {cartSummary.totalNetWeight}g net
                </span>
              </div>
            </div>
          )}
          {/* Actions */}
          <div className="space-y-2">
            <Button
              onClick={handleShowCart}
              disabled={cartItems.length === 0 || loadingCart}
              className="w-full"
              variant="outline"
            >
              <FiEye className="mr-2" />
              Show to Customer
            </Button>

            <Button
              onClick={handleCalculatePricing}
              disabled={cartItems.length === 0 || loadingCart}
              className="w-full"
            >
              Calculate Pricing
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartTab;
